import { ObjectTypes, Services } from '@diligentcorp/rc-service-object-mapping';

export const IssueTypeEnum = {
  AUDIT_FINDING: 'audit-finding',
  CONTROL_DEFICIENCY: 'control-deficiency',
  ISSUE: 'issue',
  INCIDENT: 'incident',
  EXCEPTION: 'exception',
} as const;
export const StandardIssueTypeValues = [
  IssueTypeEnum.ISSUE,
  IssueTypeEnum.INCIDENT,
  IssueTypeEnum.EXCEPTION,
] as const;

export const IssueTypeDisplayEnum = {
  AUDIT_FINDING: 'Audit finding',
  CONTROL_DEFICIENCY: 'Control deficiency',
  ISSUE: 'Issue',
  INCIDENT: 'Incident',
  EXCEPTION: 'Exception',
} as const;

export const SourceAppEnum = {
  RISK_MANAGER: Services.RISK_MANAGER,
  AUDIT: Services.AUDIT,
  INTERNAL_CONTROLS: Services.INTERNAL_CONTROLS,
  ISSUE_MANAGER: Services.ISSUE_MANAGER,
  ISSUE_MANAGEMENT: 'issue-management', // TODO: remove this when issueRelationships feature flag is removed
  ASSET_MANAGER: Services.ASSET_MANAGER,
} as const;

export const SourceEntityTypeEnum = {
  RISK: ObjectTypes.RISK,
  AUDIT: ObjectTypes.AUDIT,
  CONTROL: ObjectTypes.CONTROL,
  PROCESS: ObjectTypes.PROCESS,
  OBJECTIVE: ObjectTypes.OBJECTIVE,
  CONTROL_ASSESSMENT: ObjectTypes.CONTROL_ASSESSMENT,
  RISK_MITIGATION: ObjectTypes.RISK_MITIGATION,
  RISK_ASSESSMENT: 'risk-assessment', // Risk sends risk-assessment and ObjectTypes.RISK_EVENT_ASSESSMENT inconsistently, we have to support both
  RISK_EVENT_ASSESSMENT: ObjectTypes.RISK_EVENT_ASSESSMENT,
  ASSET: ObjectTypes.ASSET,
  ASSET_FINDING: ObjectTypes.FINDING,
  ASSET_FINDING_LEGACY: 'asset-finding',
} as const;

/**
 * Source entity types that require caching their names in the GrcObjectNameCache table.
 * These types' names are not available from SharedDB and need to be cached locally.
 */
export const CacheableSourceEntityTypes = [
  SourceEntityTypeEnum.ASSET,
  SourceEntityTypeEnum.ASSET_FINDING,
  SourceEntityTypeEnum.ASSET_FINDING_LEGACY,
] as const;

export type CacheableSourceEntityType =
  (typeof CacheableSourceEntityTypes)[number];

export const SOURCE_URL_PARAMS_KEYS = [
  'sourceApp',
  'type',
  'sourceEntityId',
  'sourceEntityName',
  'sourceEntityLink',
  'callbackUrl',
  'sourceEntityType',
] as const;

export const IssueTypeValues = Object.values(IssueTypeEnum) as [
  (typeof IssueTypeEnum)[keyof typeof IssueTypeEnum],
];
export const IssueTypeDisplayValues = Object.values(IssueTypeDisplayEnum) as [
  (typeof IssueTypeDisplayEnum)[keyof typeof IssueTypeDisplayEnum],
];
export const SourceAppValues = Object.values(SourceAppEnum) as [
  (typeof SourceAppEnum)[keyof typeof SourceAppEnum],
];
export const SourceEntityTypeValues = Object.values(SourceEntityTypeEnum) as [
  (typeof SourceEntityTypeEnum)[keyof typeof SourceEntityTypeEnum],
];

export type IssueType = (typeof IssueTypeValues)[number];
export type StandardIssueType = (typeof StandardIssueTypeValues)[number];
export type SourceAppType = (typeof SourceAppValues)[number];
export type SourceEntityType = (typeof SourceEntityTypeValues)[number];
export type SourceUrlParamsKeys = (typeof SOURCE_URL_PARAMS_KEYS)[number];

export function normalizeSourceApp(
  sourceApp: SourceAppType | null | undefined,
): string {
  if (!sourceApp) {
    return SourceAppEnum.ISSUE_MANAGER;
  }

  // Map legacy 'issue-management' to Services.ISSUE_MANAGER
  // TODO: remove this when issueRelationships feature flag is removed
  if (sourceApp === SourceAppEnum.ISSUE_MANAGEMENT) {
    return SourceAppEnum.ISSUE_MANAGER;
  }

  return sourceApp;
}

/**
 * Normalize source entity type for database persistence.
 * The DB enum uses 'finding' for asset findings;
 * Asset Manager sends 'asset-finding' in the query string.
 */
export function normalizeSourceEntityTypeForDb(
  entityType: SourceEntityType | null | undefined,
): string | null {
  if (entityType == null) return null;
  if (entityType === SourceEntityTypeEnum.ASSET_FINDING_LEGACY) {
    return SourceEntityTypeEnum.ASSET_FINDING;
  }
  return entityType;
}
